import { ScreeningConditionSet } from '@common/entities';
import { get, post } from 'config/api/config';

import { Applicant } from 'types/applicant';
import { Form } from 'types/form';
import {
  getLaunchDiagnosticQuery,
  getLaunchHttpStatus,
  recordLaunchFailure,
} from 'utils/launchDiagnostics';

const APPLICATION_ENDPOINT = '/applications';
const NEW_APPLICATION_ENDPOINT = '/viewer/newapplication';
const PROPERTY_APPLICATIONS_ENDPOINT = '/viewer/applications';
const SCREENING_CONDITIONS_ENDPOINT = '/viewer/screening-conditions';
const DECLINE_SCREENING_CONDITIONS_ENDPOINT = '/viewer/screening-conditions/decline';
const SATISFY_SCREENING_CONDITIONS_ENDPOINT = '/viewer/screening-conditions/satisfy';
const APPLICATION_COMPLETE_ENDPOINT = '/viewer/applications/complete';
const GET_ROUTE_ENDPOINT = '/application-routes';

interface GetApplication {
  applicationId: string;
  versionId: string;
  token?: string | null;
}

interface Payment {
  enabled: boolean;
  processed: boolean;
}
interface ApplicationComplete {
  propertyId: number;
  applicationId: string;
  versionId: string;
  emailAddress: string;
  payment: Payment;
  digitalSignature: string;
  ipAddress: string;
  responseId: string;
}

export const getApplication = async (data: GetApplication) => {
  const { applicationId, versionId } = data;
  const response = await get(`${APPLICATION_ENDPOINT}/${applicationId}/versions/${versionId}`);

  return response;
};

export const getRoute = async (data: GetApplication) => {
  const { applicationId, versionId } = data;
  const response = await get(`${GET_ROUTE_ENDPOINT}/${applicationId}/versions/${versionId}`);

  return response;
};

export const getApplicationForm = async (responseId: string) => {
  if (!responseId) throw new Error('Response Id is missing.');

  const response = await get<{
    applicant: Applicant;
    form: Form;
  }>(`/viewer/application/${encodeURIComponent(responseId)}`);

  return response?.data;
};

/**
 * Start new application
 * @returns promise
 */
export const postNewApplication = async (newApplicationData: any): Promise<any> => {
  const response = await post(NEW_APPLICATION_ENDPOINT, newApplicationData);
  return response;
};

export const getPropertyApplications = async (
  orgId: number,
  propertyId: number,
  bypassRoutes?: boolean,
) => {
  if (!orgId) throw new Error('Organization Id is missing.');
  if (!propertyId) throw new Error('Property Id is missing.');
  const startedAt = Date.now();
  try {
    const response = await get(
      `${PROPERTY_APPLICATIONS_ENDPOINT}/${orgId}/${propertyId}${bypassRoutes !== undefined ? `?bypassRoutes=${bypassRoutes}` : ''}`,
      { params: getLaunchDiagnosticQuery() },
    );

    if (!Array.isArray(response.data)) {
      recordLaunchFailure('applications', {
        orgId,
        propertyId,
        httpStatus: response.status,
        durationMs: Date.now() - startedAt,
      });
    }
    return response;
  } catch (error) {
    recordLaunchFailure('applications', {
      orgId,
      propertyId,
      bypassRoutes,
      durationMs: Date.now() - startedAt,
      httpStatus: getLaunchHttpStatus(error),
    });
    throw error;
  }
};

export const getScreeningConditions = async (responseId: string) => {
  const URL = `${SCREENING_CONDITIONS_ENDPOINT}/${responseId}`;
  const response = await get(URL);
  return response;
};

export const declineScreeningConditions = async (responseId: string) => {
  const URL = `${DECLINE_SCREENING_CONDITIONS_ENDPOINT}`;
  const response = await post(URL, { responseId });
  return response;
};

export const satisfyScreeningConditions = async (
  conditionSets: ScreeningConditionSet[],
  responseId: string,
) => {
  const URL = `${SATISFY_SCREENING_CONDITIONS_ENDPOINT}`;
  const response = await post(URL, {
    selectedConditionSets: conditionSets,
    responseId,
  });
  return response;
};

// Complete Application
export const applicationComplete = async (data: ApplicationComplete) => {
  const response = await post(APPLICATION_COMPLETE_ENDPOINT, data);
  return response;
};
